import { cn, tv, VariantProps } from "@utils/tw"
import { ComponentProps, ComponentPropsWithoutRef, forwardRef, useEffect, useMemo } from "react"
import { ARROW_DOWN, ARROW_UP } from "../Icons"
import { motion, useAnimationControls } from "framer-motion"
import { Typography } from "../Typography"
import { getColor, round } from "@utils/transportAffordabilityHelpers"
import { HSpace } from "@components/layout/Spacer"
import { numberWithCommas } from "@utils/numberWithCommas"
import { ContainerReset } from "@components/layout/GridReset"

export const states = {
  all: "All states",
  nsw: "NEW SOUTH WALES",
  vic: "VICTORIA",
  qld: "QUEENSLAND",
  sa: "SOUTH AUSTRALIA",
  wa: "WESTERN AUSTRALIA",
  tas: "TASMANIA",
  nt: "NORTHERN TERRITORY",
  act: "AUSTRALIAN CAPITAL TERRITORY",
}

export const ChartContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof ContainerReset>>(
  ({ className, ...props }, ref) => (
    <ContainerReset
      ref={ref}
      className={cn(
        "py-7",
        "[&_h3]:type-title-m [&_i]:type-title-m [&_h3]:inline-block [&_i]:inline-block [&_h3]:not-italic [&_i]:not-italic [&_h3]:my-5 [&_i]:my-5",
        "[&_div>p]:type-body-m",
        className,
      )}
      {...props}
    />
  ),
)
ChartContainer.displayName = "ChartContainer"

export const ToggleContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("flex justify-end gap-4", className)} {...props} />,
)
ToggleContainer.displayName = "ToggleContainer"

export const Toggle = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "border border-mid-grey w-auto float-right rounded-[20px] overflow-hidden mb-5 lg:mb-0",
      "[&_button]:appearance-none [&_button]:border-none [&_button]:bg-transparent [&_button]:type-label-s [&_button]:p-3 [&_button]:text-mid-grey",
      "[&_button:last-of-type]:border-l [&_button:last-of-type]:border-l-mid-grey",
      "[&_button.active]:bg-light-grey [&_button.active]:text-black",
      className,
    )}
    {...props}
  />
))
Toggle.displayName = "Toggle"

export const ChartPlaceholder = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex w-full h-0 relative pt-[50%] my-5",
        "[&_span]:flex [&_span]:absolute [&_span]:w-full [&_span]:h-full [&_span]:top-0 [&_span]:left-0 [&_span]:items-center [&_span]:justify-center [&_span]:type-label-caps [&_span]:p-7 [&_span]:text-center [&_span]:box-border [&_span]:text-mid-grey [&_span]:border-[3px] [&_span]:border-dashed [&_span]:border-mid-grey [&_span]:rounded-md",
        className,
      )}
      {...props}
    />
  ),
)
ChartPlaceholder.displayName = "ChartPlaceholder"

// Border colors below are ported 1:1 from utils/chartjsUtils' CHART_COLORS
// map. The non-regional ("capital") set exactly matches this project's
// state-* design tokens (state-nsw, state-vic, etc — confirmed byte-for-byte
// equal rgb() values), so those use named color classes. The darker
// "Regional / X" set has no token equivalent except National (which equals
// mid-grey), so those stay as literal arbitrary rgb() values.
const selectorStyles = tv({
  base: [
    "flex flex-col",
    "[&_label]:type-label-s [&_label]:text-mid-grey [&_label]:pb-3",
    "[&>div]:flex [&>div]:items-center [&>div]:justify-start",
    "[&>div>span]:inline-block [&>div>span]:w-[16px] [&>div>span]:h-[16px] [&>div>span]:min-w-[16px] [&>div>span]:border-4 [&>div>span]:rounded-[16px] [&>div>span]:mr-1",
    "[&>div>select]:type-title-s [&>div>select]:border-none [&>div>select]:w-1/2 [&>div>select]:min-w-[350px] [&>div>select]:bg-transparent [&>div>select]:leading-[1.5em]",
    "[&>div>select>option]:type-body-s",
  ],
  variants: {
    region: {
      National: "[&>div>span]:border-state-national",
      NSW: "[&>div>span]:border-state-nsw",
      VIC: "[&>div>span]:border-state-vic",
      QLD: "[&>div>span]:border-state-qld",
      SA: "[&>div>span]:border-state-sa",
      WA: "[&>div>span]:border-state-wa",
      TAS: "[&>div>span]:border-state-tas",
      NT: "[&>div>span]:border-state-nt",
      ACT: "[&>div>span]:border-state-act",
    },
    regional: {
      true: "",
    },
  },
  compoundVariants: [
    { region: "National", regional: true, class: "[&>div>span]:border-mid-grey" },
    { region: "NSW", regional: true, class: "[&>div>span]:border-[rgb(200,41,60)]" },
    { region: "VIC", regional: true, class: "[&>div>span]:border-[rgb(133,55,180)]" },
    { region: "QLD", regional: true, class: "[&>div>span]:border-[rgb(115,169,61)]" },
    { region: "SA", regional: true, class: "[&>div>span]:border-[rgb(202,95,170)]" },
    { region: "WA", regional: true, class: "[&>div>span]:border-[rgb(205,101,42)]" },
    { region: "TAS", regional: true, class: "[&>div>span]:border-[rgb(13,63,193)]" },
    { region: "NT", regional: true, class: "[&>div>span]:border-[rgb(215,167,0)]" },
  ],
})

export const Selector = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof selectorStyles>
>(({ region, regional, className, ...props }, ref) => (
  <div ref={ref} className={cn(selectorStyles({ region, regional }), className)} {...props} />
))
Selector.displayName = "Selector"

export const Difference = ({
  current,
  previous,
  previousQuarter,
  valueType,
  timeframe,
}: {
  current: number
  previous: number
  previousQuarter: string
  valueType?: "cost" | "income"
  timeframe: "weekly" | "yearly"
}) => {
  const value = current - previous
  if (value === 0 || round(value, 2) === "0.00") {
    return (
      <Counter>
        <Arrow>&ndash;</Arrow> <span>Steady since {previousQuarter}</span>
      </Counter>
    )
  } else {
    return (
      <Counter>
        {value > 0 ? <Arrow trend="up">{ARROW_UP}</Arrow> : <Arrow trend="down">{ARROW_DOWN}</Arrow>}
        {valueType === "income" ? (
          <>{(Math.round(value * 100) / 100).toFixed(1)}%</>
        ) : (
          <>${numberWithCommas(parseFloat(round(value, 2)), true).replace("-", "")}</>
        )}{" "}
        since {previousQuarter}
      </Counter>
    )
  }
}

const Counter = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center justify-center text-mid-grey w-full gap-2 md:gap-0", className)}
    {...props}
  />
))
Counter.displayName = "Counter"

// `--tw-grid-inner-width` is the tailwind-helper grid var equivalent of
// stitches' `$grid$inner$width`. The `env.admin` width/margin/padding split
// is expressed with the `wp:` variant (matches `.wp-admin &` /
// `.block-editor-iframe__html &`) rather than a JS ternary, since it's a
// pure CSS difference — see components/layout/GridReset.tsx and
// components/molecules/Cards.tsx for the same convention.
const timelineSelectionStyles = tv({
  base: [
    "relative w-screen wp:w-full pt-8 pb-9 bg-black box-border h-[100px] flex items-center timeline-selection",
    "ml-[calc((100vw-var(--tw-grid-inner-width))*(1/2)*-1)] wp:ml-0",
    "px-[calc((100vw-var(--tw-grid-inner-width))*(1/2))] wp:px-5",
    "md:mt-3",
    "lg:w-full lg:mx-auto lg:px-0",
  ],
  variants: {
    light: {
      true: "bg-transparent md:bg-white [&_ul_li_span]:text-black",
    },
  },
})

export const TimelineSelection = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof timelineSelectionStyles>
>(({ light, className, ...props }, ref) => (
  <div ref={ref} className={cn(timelineSelectionStyles({ light }), className)} {...props} />
))
TimelineSelection.displayName = "TimelineSelection"

// `.timeline-selection` is a stable marker class (see TimelineSelection
// above) that lets this ancestor force-reset the child's own margin-top,
// mirroring the original `[${TimelineSelection.toString()}]` stitches
// selector. Same technique as Cards.tsx's `[&_.chip]:` selectors.
export const TimelineContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("bg-black w-full md:mt-3 [&_.timeline-selection]:!mt-0", className)} {...props} />
  ),
)
TimelineContainer.displayName = "TimelineContainer"

// Vendor-prefixed range-input thumb/track chrome — the thumb backgroundImage
// is a raw, space-laden SVG data URI that can't be expressed as an arbitrary
// Tailwind class value, so it lives in stylesheets/data-timeline.scss
// instead and is referenced here as a plain classname.
const timelineInputChromeClass = "timeline-input-chrome"

const timelineStyles = tv({
  base: [
    "relative w-full",
    "[&_ul]:pointer-events-none [&_ul]:list-none [&_ul]:absolute [&_ul]:left-0 [&_ul]:top-[calc(50%-2.5px)] [&_ul]:w-full [&_ul]:flex [&_ul]:z-[1] [&_ul]:justify-between [&_ul]:!p-[0_17.2px] [&_ul]:!mt-0",
    "[&_ul_li]:w-[5px] [&_ul_li]:h-[5px] [&_ul_li]:rounded-full [&_ul_li]:bg-mid-grey [&_ul_li]:!pl-0 [&_ul_li]:!mb-0 [&_ul_li]:relative [&_ul_li::before]:hidden",
    "[&_ul_li_i]:absolute [&_ul_li_i]:top-0 [&_ul_li_i]:left-1/2 [&_ul_li_i]:[transform:translate(-50%,-200%)] [&_ul_li_i]:text-mid-grey [&_ul_li_i]:pointer-events-auto [&_ul_li_i]:not-italic",
    "[&_input]:w-full [&_input]:touch-pan-y [&_input]:cursor-pointer [&_input]:relative [&_input]:z-[2] [&_input]:block [&_input]:appearance-none [&_input]:bg-transparent [&_input]:m-0 [&_input:focus]:outline-none",
    timelineInputChromeClass,
    "lg:ml-6",
  ],
})

export const Timeline = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(timelineStyles(), className)} {...props} />
))
Timeline.displayName = "Timeline"

// Same rationale as timelineInputChromeClass above — the play/pause icon
// backgroundImages are data URIs containing raw spaces, so they live in
// stylesheets/data-timeline.scss instead.
const playTimelineArrowClass = "play-timeline-arrow"
const playTimelinePauseClass = "play-timeline-pause"

const playTimelineStyles = tv({
  base: [
    "hidden",
    "lg:flex lg:!w-auto lg:pr-6 lg:relative",
    "lg:[&_button]:flex lg:[&_button]:items-center lg:[&_button]:justify-center lg:[&_button]:whitespace-nowrap lg:[&_button]:appearance-none lg:[&_button]:bg-transparent lg:[&_button]:border-none",
    "lg:[&_button_i]:bg-[position:52%_50%] lg:[&_button_i]:bg-no-repeat lg:[&_button_i]:w-[40px] lg:[&_button_i]:h-[40px] lg:[&_button_i]:rounded-full lg:[&_button_i]:bg-yellow lg:[&_button_i]:text-[0px] lg:[&_button_i]:p-0 lg:[&_button_i]:mr-[10px] lg:[&_button_i]:inline-block",
    "lg:[&_button_span]:text-yellow lg:[&_button_span]:type-label-caps lg:[&_button_span]:inline-block",
    "lg:after:content-[''] lg:after:h-[120px] lg:after:w-px lg:after:bg-mid-grey lg:after:absolute lg:after:right-0 lg:after:top-[-35px]",
    playTimelineArrowClass,
  ],
  variants: {
    play: {
      true: playTimelinePauseClass,
    },
  },
})

export const PlayTimeline = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof playTimelineStyles>
>(({ play, className, ...props }, ref) => (
  <div ref={ref} className={cn(playTimelineStyles({ play }), className)} {...props} />
))
PlayTimeline.displayName = "PlayTimeline"

export const RollingQuarters = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex !w-auto pr-6 relative after:content-[''] after:h-[150px] after:w-px after:bg-mid-grey after:absolute after:right-0 after:top-[-50px]",
        "[&_form>div]:flex [&_form>div]:flex-col [&_form>div]:bg-dark-grey [&_form>div]:rounded-md [&_form>div]:border [&_form>div]:border-mid-grey [&_form>div]:overflow-hidden",
        className,
      )}
      {...props}
    />
  ),
)
RollingQuarters.displayName = "RollingQuarters"

export const TickDate = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span">>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        "absolute top-0 left-1/2 text-white type-label-caps whitespace-nowrap [transform:translate(-50%,24px)]",
        className,
      )}
      {...props}
    />
  ),
)
TickDate.displayName = "TickDate"

const arrowStyles = tv({
  base: [
    "inline-flex w-[18px] h-[18px] min-w-[18px] min-h-[18px] items-center justify-center rounded-[18px] overflow-hidden mr-1 relative bg-light-grey",
    "[&_svg]:w-full [&_svg]:h-full [&_svg]:z-[2] [&_svg]:relative",
    "before:content-[''] before:absolute before:inset-0 before:z-0",
  ],
  variants: {
    trend: {
      up: "[&_svg]:!text-red before:bg-red-mid",
      down: "[&_svg]:!text-green before:bg-green-mid",
    },
  },
})

export const Arrow = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span"> & VariantProps<typeof arrowStyles>>(
  ({ trend, className, ...props }, ref) => (
    <span ref={ref} className={cn(arrowStyles({ trend }), className)} {...props} />
  ),
)
Arrow.displayName = "Arrow"

export type ChartDataT = {
  location: string
  state: string
  taxonomy: "City" | "Regional"
  data: {
    cost?: number
    percentage?: number
    quarter: string
  }[]
}

export const SortedTable = ({
  data,
  cr,
  valueType,
  timeframe,
  zeroIndex,
  currentIndex,
}: {
  data: ChartDataT[]
  cr: "capital" | "regional"
  valueType: "cost" | "income"
  timeframe: "weekly" | "yearly"
  zeroIndex?: boolean
  currentIndex: number
}) => {
  const aveArray = useMemo(() => {
    if (valueType === "cost") return data.map((single) => single.data[currentIndex].cost)
    return data.map((single) => single.data[currentIndex].percentage)
  }, [data, valueType])

  const prevAveArray = useMemo(() => {
    if (zeroIndex) return null
    if (valueType === "cost") return data.map((single) => single.data[currentIndex - 1].cost)
    return data.map((single) => single.data[currentIndex - 1].percentage)
  }, [data, valueType])

  const rowAnimation = useAnimationControls()
  useEffect(() => {
    rowAnimation.set({
      y: 5,
      opacity: 0,
    })
    rowAnimation.start((i) => ({
      y: 0,
      opacity: 1,
      duration: 0.25,
      transition: {
        delay: i * 0.025,
        ease: [0.17, 0.55, 0.55, 1],
      },
    }))
  }, [data, valueType])

  if (data.length > 0)
    return (
      <>
        {cr === "capital" ? (
          <AnimatedList style={{ height: `calc(36px * ${data.length + 1})` }}>
            {data.some((e) => e.state === "NSW") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("NSW") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("NSW") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("NSW", false) }} />
                <div>
                  <Typography variant="label-s">Sydney</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "VIC") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("VIC") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("VIC") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("VIC", false) }} />
                <div>
                  <Typography variant="label-s">Melbourne</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "QLD") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("QLD") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("QLD") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("QLD", false) }} />
                <div>
                  <Typography variant="label-s">Brisbane</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "SA") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("SA") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("SA") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("SA", false) }} />
                <div>
                  {" "}
                  <Typography variant="label-s">Adelaide</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("SA")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("SA")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("SA")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("SA")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("SA")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("SA")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "WA") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("WA") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("WA") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("WA", false) }} />
                <div>
                  <Typography variant="label-s">Perth</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("WA")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("WA")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("WA")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("WA")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("WA")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("WA")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "TAS") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("TAS") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("TAS") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("TAS", false) }} />
                <div>
                  <Typography variant="label-s">Hobart</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "NT") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("NT") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("NT") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("NT", false) }} />
                <div>
                  <Typography variant="label-s">Darwin</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("NT")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("NT")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("NT")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("NT")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("NT")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("NT")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "ACT") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("ACT") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("ACT") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("ACT", false) }} />
                <div>
                  <Typography variant="label-s">Canberra</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("ACT")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("ACT")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("ACT")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("ACT")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("ACT")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("ACT")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            <Row style={{ top: `${data.length * 36}px` }}>
              <div>
                <Typography variant="text-s">&nbsp;</Typography>
              </div>
              <Dot style={{ background: getColor("NATIONAL", false) }} />
              <div>
                <Typography variant="label-s">Capital Avg.</Typography>
              </div>
              {aveArray.length > 0 && (
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          Math.round((aveArray.reduce((a, b) => a! + b!, 0)! / aveArray.length) * 100) / 100,
                          true,
                        )}
                      </>
                    ) : (
                      <>{(aveArray.reduce((a, b) => a! + b!, 0)! / aveArray.length).toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
              )}
              <TableDifference
                current={aveArray.reduce((a, b) => a! + b!, 0)! / aveArray.length}
                previous={prevAveArray ? prevAveArray.reduce((a, b) => a! + b!, 0)! / prevAveArray.length : undefined}
                valueType={valueType}
                timeframe={timeframe}
              />
            </Row>
          </AnimatedList>
        ) : (
          <AnimatedList style={{ height: `calc(36px * ${data.length + 1})` }}>
            {data.some((e) => e.state === "NSW") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("NSW") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("NSW") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("NSW", true) }} />
                <div>
                  <Typography variant="label-s">Wagga Wagga</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("NSW")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "VIC") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("VIC") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("VIC") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("VIC", true) }} />
                <div>
                  <Typography variant="label-s">Geelong</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("VIC")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "QLD") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("QLD") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("QLD") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("QLD", true) }} />
                <div>
                  <Typography variant="label-s">Townsville</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("QLD")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "SA") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("SA") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("SA") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("SA", true) }} />
                <div>
                  {" "}
                  <Typography variant="label-s">Mount Gambier</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("SA")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("SA")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("SA")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("SA")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("SA")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("SA")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "WA") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("WA") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("WA") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("WA", true) }} />
                <div>
                  <Typography variant="label-s">Bunbury</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("WA")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("WA")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("WA")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("WA")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("WA")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("WA")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "TAS") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("TAS") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("TAS") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("TAS", true) }} />
                <div>
                  <Typography variant="label-s">Launceston</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("TAS")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            {data.some((e) => e.state === "NT") && (
              <Row style={{ top: `${data.map((e) => e.state).indexOf("NT") * 36}px` }}>
                <div>
                  <Typography variant="text-s">{data.map((e) => e.state).indexOf("NT") + 1}</Typography>
                </div>
                <Dot style={{ background: getColor("NT", true) }} />
                <div>
                  <Typography variant="label-s">Alice Springs</Typography>
                </div>
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          data[data.map((e) => e.state).indexOf("NT")].data[currentIndex].cost as number,
                          true,
                        )}
                      </>
                    ) : (
                      <>{data[data.map((e) => e.state).indexOf("NT")].data[currentIndex].percentage?.toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
                <TableDifference
                  current={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("NT")].data[currentIndex].cost!
                      : data[data.map((e) => e.state).indexOf("NT")].data[currentIndex].percentage!
                  }
                  previous={
                    valueType === "cost"
                      ? data[data.map((e) => e.state).indexOf("NT")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .cost!
                      : data[data.map((e) => e.state).indexOf("NT")].data[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
                          .percentage!
                  }
                  valueType={valueType}
                  timeframe={timeframe}
                />
              </Row>
            )}

            <Row style={{ top: `${data.length * 36}px` }}>
              <div>
                <Typography variant="text-s">&nbsp;</Typography>
              </div>
              <Dot style={{ background: getColor("NATIONAL", true) }} />
              <div>
                <Typography variant="label-s">Regional Avg.</Typography>
              </div>
              {aveArray.length > 0 && (
                <div>
                  <Typography variant="text-s">
                    {valueType === "cost" ? (
                      <>
                        $
                        {numberWithCommas(
                          Math.round((aveArray.reduce((a, b) => a! + b!, 0)! / aveArray.length) * 100) / 100,
                          true,
                        )}
                      </>
                    ) : (
                      <>{(aveArray.reduce((a, b) => a! + b!, 0)! / aveArray.length).toFixed(1)}%</>
                    )}
                  </Typography>
                </div>
              )}
              <TableDifference
                current={aveArray.reduce((a, b) => a! + b!, 0)! / aveArray.length}
                previous={prevAveArray ? prevAveArray.reduce((a, b) => a! + b!, 0)! / prevAveArray.length : undefined}
                valueType={valueType}
                timeframe={timeframe}
              />
            </Row>
          </AnimatedList>
        )}
      </>
    )

  return <></>
}

const AnimatedList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col w-full relative my-4", className)} {...props} />
))
AnimatedList.displayName = "AnimatedList"

const Row = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex w-full h-[36px] py-2 items-center absolute transition-all duration-[350ms] ease-movement",
      "[&>div:nth-of-type(1)]:w-[15px] [&>div:nth-of-type(2)]:w-[150px] [&>div:nth-of-type(3)]:w-[100px]",
      className,
    )}
    {...props}
  />
))
Row.displayName = "Row"

const Dot = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span">>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn("inline-block w-[10px] h-[10px] min-w-[10px] min-h-[10px] rounded-[10px] mx-3", className)}
    {...props}
  />
))
Dot.displayName = "Dot"

const TableDifference = ({
  current,
  previous,
  valueType,
  timeframe,
}: {
  current: number
  previous?: number
  valueType: "cost" | "income"
  timeframe: "weekly" | "yearly"
}) => {
  const value = previous === undefined ? 0 : current - previous

  if (value === 0 || round(value, 2) === "0.00") {
    return <Typography variant="label-s">&ndash;</Typography>
  } else {
    return (
      <TDText>
        {value > 0 ? <Arrow trend="up">{ARROW_UP}</Arrow> : <Arrow trend="down">{ARROW_DOWN}</Arrow>}
        <Typography variant="label-s" className={value > 0 ? "text-red" : "text-green"}>
          {valueType === "cost" && (
            <>${numberWithCommas(parseFloat((Math.round(value * 100) / 100).toFixed(2).replace("-", "")), true)}</>
          )}
          {valueType === "income" && <>{(Math.round(value * 100) / 100).toFixed(1).replace("-", "")}%</>}
        </Typography>
      </TDText>
    )
  }
}

export const TDText = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center", className)} {...props} />
))
TDText.displayName = "TDText"

export const EVTableContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof ContainerReset>>(
  ({ className, ...props }, ref) => (
    <ContainerReset
      ref={ref}
      className={cn(
        "py-6",
        "[&_div>p_a]:text-yellow [&_div>p_a]:underline",
        "[&_table]:w-full [&_table]:text-center [&_table]:type-body-s [&_table]:table-fixed",
        "[&_table_td]:py-2 [&_table_th]:py-2 [&_table_td]:cursor-pointer [&_table_th]:cursor-pointer",
        "[&_table_td_a]:text-dark-grey [&_table_th_a]:text-dark-grey [&_table_td_a]:inline-flex [&_table_th_a]:inline-flex [&_table_td_a]:items-center [&_table_th_a]:items-center",
        "[&_table_thead]:bg-grey",
        "[&_table_tbody_tr]:bg-light-grey [&_table_tbody_tr]:transition-all [&_table_tbody_tr]:duration-[350ms] [&_table_tbody_tr]:ease-movement",
        "[&_table_tbody_tr:nth-of-type(2n)]:bg-white",
        "[&_table_tbody_tr:hover]:bg-yellow",
        "[&_table_tfoot]:bg-black [&_table_tfoot]:text-white",
        className,
      )}
      {...props}
    />
  ),
)
EVTableContainer.displayName = "EVTableContainer"

export const TT = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center w-full justify-center [&_svg]:min-w-[24px] [&_svg]:min-h-[24px]", className)}
    {...props}
  />
))
TT.displayName = "TT"

// Active-state icon backgrounds mirror utils/evDataHelpers' `colors` map.
// ICE (rgb(142,142,142)) and Hybrid (rgb(255,237,72)) happen to be exact
// matches for the design system's mid-grey / alt-yellow-light tokens, so
// those two use named color classes; BEV/PHEV/HFCEV have no token
// equivalent and stay as literal arbitrary values.
export const FuelTypeFilter = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "[&_ul]:flex [&_ul]:justify-between [&_ul]:list-none [&_ul]:gap-4",
        "[&_ul_li_button]:w-[40px] [&_ul_li_button]:flex [&_ul_li_button]:flex-col [&_ul_li_button]:items-center [&_ul_li_button]:justify-center [&_ul_li_button]:appearance-none [&_ul_li_button]:border-none [&_ul_li_button]:gap-2 [&_ul_li_button]:bg-transparent",
        "[&_ul_li_button_i]:flex [&_ul_li_button_i]:flex-col [&_ul_li_button_i]:items-center [&_ul_li_button_i]:justify-center [&_ul_li_button_i]:w-[40px] [&_ul_li_button_i]:h-[40px] [&_ul_li_button_i]:rounded-[40px] [&_ul_li_button_i]:bg-light-grey [&_ul_li_button_i]:my-0 [&_ul_li_button_i]:py-0",
        "[&_ul_li_button_i_svg]:w-[20px] [&_ul_li_button_i_svg]:h-[20px]",
        "[&_ul_li_button_span]:type-label-s [&_ul_li_button_span]:text-center [&_ul_li_button_span]:whitespace-nowrap",
        "[&_ul_li_button.active.all_i]:bg-yellow",
        "[&_ul_li_button.active.ice_i]:bg-mid-grey",
        "[&_ul_li_button.active.bev_i]:bg-[rgb(96,124,222)]",
        "[&_ul_li_button.active.phev_i]:bg-[rgb(255,147,68)]",
        "[&_ul_li_button.active.hybrid_i]:bg-alt-yellow-light",
        "[&_ul_li_button.active.hfcev_i]:bg-[rgb(151,229,245)]",
        "sm:[&_ul_li_button]:w-[60px]",
        "sm:[&_ul_li_button_i]:w-[60px] sm:[&_ul_li_button_i]:h-[60px] sm:[&_ul_li_button_i]:rounded-[60px]",
        "sm:[&_ul_li_button_i_svg]:w-[40px] sm:[&_ul_li_button_i_svg]:h-[40px]",
        className,
      )}
      {...props}
    />
  ),
)
FuelTypeFilter.displayName = "FuelTypeFilter"

export const Switch = forwardRef<HTMLFormElement, ComponentPropsWithoutRef<"form">>(({ className, ...props }, ref) => (
  <form
    ref={ref}
    className={cn(
      "block relative",
      "[&>div]:flex [&>div]:flex-col [&>div]:bg-white [&>div]:rounded-md [&>div]:border [&>div]:border-mid-grey [&>div]:overflow-hidden",
      className,
    )}
    {...props}
  />
))
Switch.displayName = "Switch"

const switchRowStyles = tv({
  base: "flex",
  variants: {
    border: {
      true: "border-t border-t-mid-grey",
    },
  },
})

export const SwitchRow = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof switchRowStyles>
>(({ border, className, ...props }, ref) => (
  <div ref={ref} className={cn(switchRowStyles({ border }), className)} {...props} />
))
SwitchRow.displayName = "SwitchRow"

const switchSelectionStyles = tv({
  base: [
    "flex flex-col grow border-l border-l-mid-grey first-of-type:border-l-0",
    "[&_input]:hidden [&_label:has(input:checked)]:bg-yellow [&_label:has(input:checked)]:text-black",
    "[&_label]:type-label-s [&_label]:py-2 [&_label]:px-3 [&_label]:w-full [&_label]:h-full [&_label]:cursor-pointer [&_label]:whitespace-nowrap [&_label]:flex [&_label]:items-center [&_label]:justify-center [&_label]:text-center",
  ],
  variants: {
    border: {
      true: "border-l border-l-mid-grey",
    },
    noBorder: {
      true: "border-l-0",
    },
    borderTop: {
      true: "border-t border-t-mid-grey",
    },
  },
})

export const SwitchSelection = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof switchSelectionStyles>
>(({ border, noBorder, borderTop, className, ...props }, ref) => (
  <div ref={ref} className={cn(switchSelectionStyles({ border, noBorder, borderTop }), className)} {...props} />
))
SwitchSelection.displayName = "SwitchSelection"

const switchGroupStyles = tv({
  base: "flex border-t border-t-mid-grey",
  variants: {
    noBorder: {
      true: "border-t-0",
    },
  },
})

export const SwitchGroup = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof switchGroupStyles>
>(({ noBorder, className, ...props }, ref) => (
  <div ref={ref} className={cn(switchGroupStyles({ noBorder }), className)} {...props} />
))
SwitchGroup.displayName = "SwitchGroup"

export const SwitchColumn = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col w-[calc(100%*(1/2))] max-w-[calc(100%*(1/2))]",
        "[&>div]:border-t [&>div]:border-t-mid-grey",
        "[&>div:first-of-type]:border-l [&>div:first-of-type]:border-l-mid-grey [&>div:first-of-type]:border-t-0",
        className,
      )}
      {...props}
    />
  ),
)
SwitchColumn.displayName = "SwitchColumn"

export const FilterBlock = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("w-full flex items-center justify-center gap-6 flex-wrap", className)} {...props} />
  ),
)
FilterBlock.displayName = "FilterBlock"

export const vehicleTypes = {
  all: "All vehicle types",
  car: "Car",
  car_small: "Small Car",
  car_medium: "Medium Car",
  car_large: "Large Car",
  car_sports: "Sports Car",
  people_mover: "People Mover",
  suv: "SUV",
  suv_small: "Small SUV",
  suv_medium: "Medium SUV",
  suv_large: "Large SUV",
  ute: "Ute",
  ute_2wd: "Ute (2WD)",
  ute_4wd: "Ute (4WD)",
  van: "Van",
}

const floatingFilterStyles = tv({
  base: [
    "fixed z-50 bottom-0 left-0 w-screen bg-light-white rounded-t-[12px]",
    "pt-8 pr-4 pb-4 pl-4 flex flex-col items-stretch gap-4",
    "transition-all duration-[350ms] ease-movement",
    "translate-y-[calc(100%-var(--spacing-8))]",
    "shadow-[0px_6px_12px_10px_rgba(34,34,34,0.08),0px_1px_3px_2px_rgba(34,34,34,0.1)]",
    "[&>button]:absolute [&>button]:top-0 [&>button]:left-1/2 [&>button]:-translate-x-1/2 [&>button]:-translate-y-1/2 [&>button]:uppercase [&>button]:font-extrabold",
  ],
  variants: {
    open: {
      true: "translate-y-0",
    },
  },
})

export const FloatingFilter = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof motion.div> & VariantProps<typeof floatingFilterStyles>
>(({ open, className, ...props }, ref) => (
  <motion.div ref={ref} className={cn(floatingFilterStyles({ open }), className)} {...props} />
))
FloatingFilter.displayName = "FloatingFilter"

export const TableWrapper = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "[&>div]:overflow-x-auto [&>div]:w-full [&>div]:flex [&>div]:flex-col",
        "[&_th.sortable_i]:w-6 [&_th.sortable_i]:h-6 [&_th.sortable_i]:hidden",
        "[&_th.sortable:hover]:bg-yellow [&_th.sortable:hover_i]:inline",
        // Fixed 768px breakpoint doesn't match this project's sm(600)/md(900)
        // scale — ported verbatim as an arbitrary media query rather than
        // snapping it onto the nearest configured breakpoint.
        "[@media(max-width:768px)]:[&_table]:!w-auto",
        "[@media(max-width:768px)]:[&_th]:!min-w-[100px] [@media(max-width:768px)]:[&_td]:!min-w-[100px]",
        "[@media(max-width:768px)]:[&_th]:whitespace-nowrap [@media(max-width:768px)]:[&_td]:whitespace-nowrap",
        "[@media(max-width:768px)]:[&_th]:px-2 [@media(max-width:768px)]:[&_td]:px-2",
        className,
      )}
      {...props}
    />
  ),
)
TableWrapper.displayName = "TableWrapper"

export const InnerBlockContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => <div ref={ref} className={className} {...props} />,
)
InnerBlockContainer.displayName = "InnerBlockContainer"
